import { useState, useEffect } from 'react';
import { fetchWithAuth } from '../utils/fetchWithAuth';

function IssueComments({ issueKey, currentUser, onImageClick }) {
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [newCommentText, setNewCommentText] = useState('');
  const [addingComment, setAddingComment] = useState(false);
  const [deletingCommentId, setDeletingCommentId] = useState(null);
  const [expandedComments, setExpandedComments] = useState({});

  useEffect(() => {
    if (issueKey) {
      fetchComments();
    }
  }, [issueKey]);

  const fetchComments = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await fetchWithAuth(`issues/${encodeURIComponent(issueKey)}/comments`);
      const data = await response.json();

      if (response.ok && data.success) {
        setComments(data.data.comments || []);
      } else {
        throw new Error(data.error || 'Failed to fetch comments');
      }
    } catch (err) {
      console.error('Error fetching comments:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleAddComment = async (e) => {
    e.preventDefault();

    if (!newCommentText.trim()) {
      return;
    }

    try {
      setAddingComment(true);
      setError(null);

      // Inject the user name in the format [firstname lastname] at the beginning of the comment
      // This convention allows the backend to extract the author name
      const userDisplayName = currentUser?.name || 'Utilisateur';
      const commentTextWithAuthor = `[${userDisplayName}] ${newCommentText.trim()}`;

      const response = await fetchWithAuth(`issues/${encodeURIComponent(issueKey)}/comments`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          text: commentTextWithAuthor,
        })
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setNewCommentText('');
        // Refresh comments list
        await fetchComments();
      } else {
        throw new Error(data.error || 'Failed to add comment');
      }
    } catch (err) {
      console.error('Error adding comment:', err);
      setError(err.message);
    } finally {
      setAddingComment(false);
    }
  };

  const handleDeleteComment = async (commentId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce commentaire ?')) {
      return;
    }

    try {
      setDeletingCommentId(commentId);
      setError(null);

      const response = await fetchWithAuth(
        `issues/${encodeURIComponent(issueKey)}/comments/${encodeURIComponent(commentId)}`,
        {
          method: 'DELETE',
        }
      );

      const data = await response.json();

      if (response.ok && data.success) {
        // Refresh comments list
        await fetchComments();
      } else {
        throw new Error(data.error || 'Failed to delete comment');
      }
    } catch (err) {
      console.error('Error deleting comment:', err);
      setError(err.message);
    } finally {
      setDeletingCommentId(null);
    }
  };

  const toggleComment = (commentIndex) => {
    setExpandedComments(prev => ({
      ...prev,
      [commentIndex]: !prev[commentIndex]
    }));
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      <h3 className="text-lg font-semibold text-gray-900 mb-4 border-b border-gray-100 pb-2">
        Commentaires ({comments.length})
      </h3>

      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded text-red-700 text-sm">
          {error}
        </div>
      )}

      {/* Add comment form */}
      <form onSubmit={handleAddComment} className="mb-6">
        <div className="mb-3">
          <textarea
            value={newCommentText}
            onChange={(e) => setNewCommentText(e.target.value)}
            placeholder="Ajouter un commentaire..."
            rows={4}
            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary focus:border-primary resize-none"
            disabled={addingComment}
          />
        </div>
        <button
          type="submit"
          disabled={addingComment || !newCommentText.trim()}
          className="px-4 py-2 bg-black text-white rounded hover:bg-gray-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {addingComment ? 'Ajout...' : 'Ajouter un commentaire'}
        </button>
      </form>

      {/* Comments list */}
      {loading ? (
        <div className="flex justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      ) : comments.length > 0 ? (
        <div className="space-y-2">
          {comments.map((comment, index) => {
            const isExpanded = expandedComments[index];
            const isDeleting = deletingCommentId === comment.id;

            return (
              <div key={comment.id || index} className="border border-gray-100 rounded-lg">
                <button
                  onClick={() => toggleComment(index)}
                  className="w-full flex items-center justify-between p-3 text-left hover:bg-gray-50 rounded-lg transition-colors"
                  disabled={isDeleting}
                >
                  <div className="flex items-center flex-1">
                    <span className="text-sm font-medium text-gray-900">
                      {comment.author || 'Utilisateur'}
                    </span>
                    <span className="ml-2 text-xs text-gray-500">
                      {comment.created ? new Date(comment.created).toLocaleString('fr-FR') : ''}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    {isDeleting && (
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary"></div>
                    )}
                    <svg
                      className={`w-5 h-5 text-gray-500 transform transition-transform ${
                        isExpanded ? 'rotate-90' : ''
                      }`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 5l7 7-7 7"
                      />
                    </svg>
                  </div>
                </button>

                {isExpanded && (
                  <div className="px-3 pb-3 border-t border-gray-100 mt-2 pt-3">
                    {comment.text && (
                      <div className="text-sm text-gray-700 mb-3 whitespace-pre-wrap">
                        {comment.text}
                      </div>
                    )}

                    {comment.images && comment.images.length > 0 && (
                      <div className="flex flex-wrap gap-2 mt-3">
                        {comment.images.map((image, imgIndex) => (
                          <div
                            key={imgIndex}
                            className="border border-gray-300 rounded cursor-pointer"
                            onClick={() => onImageClick && onImageClick({
                              url: image.url || image.src || image,
                              filename: image.filename || `Comment image ${imgIndex + 1}`
                            })}
                          >
                            <img
                              src={image.url || image.src || image}
                              alt={image.filename || `Comment image ${imgIndex + 1}`}
                              className="w-20 h-20 object-cover rounded"
                              onError={(e) => {
                                e.target.style.display = 'none';
                              }}
                            />
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Delete button - only show if user has permission (admin/manager or comment author) */}
                    {(currentUser?.role === 'admin' || currentUser?.role === 'manager' ||
                      (currentUser?.username && comment.author &&
                       currentUser.username.toLowerCase() === comment.author.toLowerCase())) && (
                      <div className="mt-3 pt-3 border-t border-gray-100">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteComment(comment.id);
                          }}
                          disabled={isDeleting}
                          className="text-sm text-red-600 hover:text-red-800 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {isDeleting ? 'Suppression...' : 'Supprimer'}
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      ) : (
        <p className="text-gray-500 text-center py-8">Aucun commentaire</p>
      )}
    </div>
  );
}

export default IssueComments;

